<template>
  <div class="xpage">
    <ValidationObserver v-slot="{ invalid }" ref="formValidationObserver">
      <!--Header-->
      <div class="page-header flex-shrink-0 shadow-sm">
        <div class="container-fluid px-md-4 px-3">
          <div class="row no-gutters d-flex justify-content-between align-items-center">
            <h3 class="mb-0">
              <i class="fas fa-layer-group"></i>&nbsp;{{ isNew ? 'Add' : 'Edit' }} Policy Container
              <b-spinner class="ml-2" v-show="isBusy" small label="Small Spinner"></b-spinner>
            </h3>
            <b-form inline>
              <div v-if="!isNew">
                <b-form-group>
                  <div class="d-flex mr-3">
                    <b-form-checkbox v-model="container.isArchived" name="check-button" switch></b-form-checkbox>
                    <label class="control-label mr-2">Archive Container</label>
                  </div>
                </b-form-group>
              </div>
              <div>
                <b-btn variant="secondary" @click="returnToPrevious">Cancel</b-btn>
                <b-btn variant="primary" @click="doSavePolicyContainer" :disabled="invalid || isBusy">Save</b-btn>
              </div>
            </b-form>
          </div>
        </div>
      </div>

      <!--Content-->
      <div class="container-fluid p-4">
        <div class="form-container p-4 mb-3 radius-bottom">
          <div class="d-flex align-items-center mb-4">
            <span class="fa-stack">
              <i class="fas fa-circle fa-stack-2x text-secondary" aria-hidden="true"></i>
              <i class="fas fa-list-ul fa-stack-1x fa-inverse" aria-hidden="true"></i>
            </span>
            <h5 class="text-dark m-0 pl-2">Container Details</h5>
          </div>

          <div class="row">
            <div class="col-lg-6 col-12">
              <b-form-group>
                <ValidationProvider name="Name" rules="required" v-slot="validation" immediate>
                  <label class="control-label">Name</label>
                  <b-form-input
                    type="text"
                    placeholder="Container Name"
                    v-model="container.name"
                    :state="spr2_getValidationState(validation)"
                  ></b-form-input>
                </ValidationProvider>
              </b-form-group>
            </div>

            <div class="col-lg-3 col-md-6 col-12">
              <b-form-group>
                <label class="control-label">Start Date</label>
                <b-form-input
                  class="form-control"
                  type="date"
                  :value="container.startDate"
                  name="startDate"
                  id="startDate"
                  onclick="this.showPicker()"
                  @change="onStartDateChange($event)"
                ></b-form-input>
              </b-form-group>
            </div>

            <div class="col-lg-3 col-md-6 col-12">
              <b-form-group>
                <label class="control-label">End Date</label>
                <b-form-input
                  class="form-control"
                  type="date"
                  :value="container.endDate"
                  name="endDate"
                  id="endDate"
                  onclick="this.showPicker()"
                  @change="container.endDate = $event"
                ></b-form-input>
              </b-form-group>
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <b-form-group>
                <label class="control-label">Notes</label>
                <b-form-textarea
                  v-model="container.notes"
                  placeholder="Additional notes or comments"
                  rows="3"
                  max-rows="6"
                ></b-form-textarea>
              </b-form-group>
            </div>
          </div>

          <div class="row" v-if="isNew">
            <div class="col-12">
              <b-form-group>
                <b-form-checkbox v-model="container.isArchived" switch>
                  Archived
                </b-form-checkbox>
              </b-form-group>
            </div>
          </div>
        </div>

        <!-- Layer Tower Editor -->
        <div class="form-container p-4 mb-3 radius-bottom">
          <div class="d-flex align-items-center mb-4">
            <span class="fa-stack">
              <i class="fas fa-circle fa-stack-2x text-secondary" aria-hidden="true"></i>
              <i class="fas fa-layer-group fa-stack-1x fa-inverse" aria-hidden="true"></i>
            </span>
            <h5 class="text-dark m-0 pl-2">Layer Tower</h5>
          </div>
          <layer-tower-editor ref="layerTowerEditor" v-model="layers" :policies="availablePolicies"></layer-tower-editor>
          <b-alert variant="warning" :show="showDirtyLayerWarning" class="mt-3 mb-0">
            <i class="fas fa-exclamation-triangle mr-1"></i>
            You have unsaved layer changes. Please click <strong>Save Layer</strong> before saving the container.
          </b-alert>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import { spr2Validation } from '@/mixins/Spr2SharedFunctions'
import { mapActions } from 'vuex'
import LayerTowerEditor from '@/components/policies/LayerTowerEditor.vue'

export default {
  mixins: [spr2Validation],
  components: {
    LayerTowerEditor
  },
  name: 'AddEditPolicyContainer',
  data () {
    return {
      isBusy: false,
      showDirtyLayerWarning: false,
      container: {
        id: null,
        name: null,
        startDate: null,
        endDate: null,
        policyIds: [],
        isArchived: false,
        notes: null
      },
      layers: [],
      availablePolicies: []
    }
  },
  computed: {
    isNew () { return !this.$route.params.id }
  },
  async mounted () {
    const containerId = this.$route.params.id
    if (containerId) {
      await this.doGetPolicyContainer(containerId)
    }
    // Best-effort: load available policies for the layer tower editor
    try {
      const result = await this.getInsurancePolicies({})
      if (result && result.data && result.data.policies) {
        this.availablePolicies = result.data.policies
      } else if (result && result.data && Array.isArray(result.data)) {
        this.availablePolicies = result.data
      }
    } catch (e) {
      // Not critical — empty array is fine
    }
  },
  methods: {
    ...mapActions('company', ['getPolicyContainerForEdit', 'savePolicyContainer', 'getInsurancePolicies']),

    async doGetPolicyContainer (containerId) {
      this.isBusy = true
      try {
        const result = await this.getPolicyContainerForEdit(containerId)
        const data = (result && result.data && result.data.container) ? result.data.container : {}
        Object.assign(this.container, data)
        if (data.layers && Array.isArray(data.layers)) {
          this.layers = data.layers
        }
      } catch (e) {
        console.error('Error loading policy container:', e)
      } finally {
        this.isBusy = false
      }
    },

    async doSavePolicyContainer () {
      // Guard: warn if a layer is still open for editing
      if (this.$refs.layerTowerEditor && this.$refs.layerTowerEditor.editingIndex != null) {
        this.showDirtyLayerWarning = true
        return
      }
      this.showDirtyLayerWarning = false

      if (!this.container.name) {
        return
      }
      this.isBusy = true
      try {
        const payload = Object.assign({}, this.container, { layers: this.layers })
        const result = await this.savePolicyContainer(payload)
        if (result && result.data && result.data.success) {
          this.$toasted.success(result.data.message || 'Policy container saved successfully.', { duration: 4000 })
          this.$router.push('/policycontainers')
        } else {
          const msg = (result && result.data && result.data.message) ? result.data.message : 'An error occurred while saving.'
          this.$toasted.error(msg, { duration: 4000 })
        }
      } catch (e) {
        this.$toasted.error('An error occurred while saving.', { duration: 4000 })
      } finally {
        this.isBusy = false
      }
    },

    onStartDateChange (dateStr) {
      this.container.startDate = dateStr
      if (dateStr && !this.container.endDate) {
        const d = new Date(dateStr + 'T00:00:00')
        d.setFullYear(d.getFullYear() + 1)
        const yyyy = d.getFullYear()
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const dd = String(d.getDate()).padStart(2, '0')
        this.container.endDate = `${yyyy}-${mm}-${dd}`
      }
    },

    returnToPrevious () {
      this.$router.push('/policycontainers')
    }
  }
}
</script>
